import { QueryClient, QueryKey } from '@tanstack/react-query';
import { Response } from '@dealstack/shared/lib/common/dataExchange/apiV1';

export function replaceCachePayload<TPayload>(
	queryClient: QueryClient,
	queryKey: QueryKey,
	payload: TPayload
): void {
	queryClient.setQueryData<Response<TPayload>>(queryKey, (old) => ({
		...(old ?? {}),
		payload,
	}));
}

export function removeCacheArrayItem<TItem extends { id: string }>(
	queryClient: QueryClient,
	queryKey: QueryKey,
	id: string
): void {
	queryClient.setQueryData<Response<TItem[]>>(queryKey, (old) => {
		if (!old?.payload || !Array.isArray(old.payload)) {
			return old;
		}
		return {
			...old,
			payload: old.payload.filter((item) => item.id !== id),
		};
	});
}

export function patchCacheArrayItem<TItem extends { id: string }>(
	queryClient: QueryClient,
	queryKey: QueryKey,
	updatedItem: TItem
): void {
	queryClient.setQueryData<Response<TItem[]>>(queryKey, (old) => {
		if (!old?.payload || !Array.isArray(old.payload)) {
			return old;
		}
		return {
			...old,
			payload: old.payload.map((item) => (item.id === updatedItem.id ? updatedItem : item)),
		};
	});
}

export function patchCacheNestedArrayItem<
	TParent extends { id: string },
	TChild extends { id: string },
>(
	queryClient: QueryClient,
	queryKey: QueryKey,
	parentId: string,
	childKey: keyof TParent,
	updatedChild: TChild
): void {
	queryClient.setQueryData<Response<TParent[]>>(queryKey, (old) => {
		if (!old?.payload || !Array.isArray(old.payload)) {
			return old;
		}
		return {
			...old,
			payload: old.payload.map((parent) => {
				if (parent.id !== parentId) {
					return parent;
				}
				const children = parent[childKey];
				if (!Array.isArray(children)) {
					return parent;
				}
				return {
					...parent,
					[childKey]: children.map((child: TChild) =>
						child.id === updatedChild.id ? updatedChild : child
					),
				};
			}),
		};
	});
}
